import { Component } from "@kluntje/core";
import { fetchJSON } from "Helper/apiHelper";
import { getDocLang, getDocLocale } from "Helper/metaHelper";
import { render } from "lit-html";
import { I18nService } from "Services/I18nService";
import { FlightsApiResponse } from "Interfaces/flightsInformation";
import { addClass, removeClass, hasClass, find, toggleClass } from "@kluntje/js-utils/lib/dom-helpers";
import { getCurrentMQ } from "Helper/domHelper";
import { ACTIVE, DISABLED, STICKY } from "Helper/cssClasses";
import { throttle } from "Helper/functionHelper/decorators";
import { ViewportObserver } from "@kluntje/services";

// eslint-disable-next-line import/extensions
import { errorMessageTemplate, flightsTemplate, noResultsMessageTemplate } from "./fra-m-flights__row.template";

/**
 * Display flight schedule
 */
interface RequestInfo {
  q: string;
  flighttype: string;
  key: string;
  type: string;
  time: string;
}

class FRAFlights extends Component {
  viewportObserver = ViewportObserver.getInstance();

  constructor() {
    super({
      ui: {
        table: ".fra-e-table :-one",
        tableHeader: ".fra-e-table__header :-one",
        tableBody: ".fra-e-table__body :-one",
        loadMoreBtn: ".fra-m-flights__more :-one",
        prevPageBtn: ".fra-m-flights__prev :-one",
        tableFlighttypeLabel: ".fra-e-table__header .fra-js-flighttype-label :-one",
        tableTerminalLabel: ".fra-e-table__header .fra-js-terminal-label :-one",
        form: ".fra-m-flights-search :-one",
        forms: ".fra-m-flights-search",
        flightSearchErrorMessage: ".fra-m-flights-search__error :-one",
        flightSearchNoResultsMessage: ".fra-m-flights__no-results :-one",
        flightSearch: ".fra-m-flights__search-grid :-one",
        flightSearchTab: ".fra-m-flights__search-grid .fra-m-tab :-one",
      },

      events: [
        {
          event: "click",
          target: "loadMoreBtn",
          handler: "handleLoadMore",
        },
        {
          event: "click",
          target: "prevPageBtn",
          handler: "handlePrevPage",
        },
        {
          event: "fra-formdata-changed",
          target: "forms",
          handler: "handleFormUpdate",
        },
        {
          event: "fra-time-picker-dialog",
          target: "window",
          handler: "toggleActiveDialogEvent",
        },
        {
          event: "scroll",
          target: "window",
          handler: "onScroll",
        },
        {
          event: "kl-in-vp",
          target: "this",
          handler: "onComponentInViewport",
        },
        {
          event: "kl-out-vp",
          target: "this",
          handler: "onComponentOutOfViewport",
        },
      ],
      initialStates: {
        flights: [],
        currentPage: 1,
        prevPage: 0,
        isPrevFlightReq: false,
        previousRequest: "",
        request: {},
      },
      reactions: {
        flights: ["renderTable"],
        currentPage: ["fetchFlights"],
        request: ["fetchFlights"],
      },
      preserveChildren: true,
    });
    this.viewportObserver.observe(this);
    this.activeForm = "";
  }

  static readonly DEFAULT_ENTRIES_COUNT = 10;

  static readonly DEFAULT_INITIAL_PAGE = 1;

  static readonly DEFAULT_INITIAL_LANG = getDocLang();

  static readonly DEFAULT_INITIAL_LOCALE = getDocLocale();

  static readonly AUTO_SCROLL = "fra-auto-scroll";
  // component is in viewport
  private _inViewport = false;

  private lastScrollTopPosition = 0;

  afterComponentRender(): void {
    // unload and beforeunload prevent forward-backword caching issues
    // eslint-disable-next-line
    window.addEventListener("unload", function () {});
    // eslint-disable-next-line
    window.addEventListener("beforeunload", function () {});
    // to handle event fra-in-vp/fra-out-vp:
    this.viewportObserver.observe(this);
    this.lastScrollTopPosition = window.scrollY;
    const observer = new IntersectionObserver(
      async ([e]) => {
        await new Promise(res => setTimeout(res, 250));
        toggleClass(this.ui.flightSearch, "fra-state-stuck", e.intersectionRatio < 1);
      },
      { threshold: [1] },
    );
    observer.observe(this.ui.form);
  }

  /**
   * handles if component is in viewport
   */
  onComponentInViewport(): void {
    this._inViewport = true;
  }

  /**
   * handles if component is out of viewport
   */
  onComponentOutOfViewport(): void {
    this._inViewport = false;
  }

  /**
   * throttle scrolling (and show flightSearchTab in MQ1-2) when component is in viewport
   */
  @throttle(250)
  onScroll(): void {
    if (!this._inViewport) return;
    if (getCurrentMQ() > "MQ2") return;

    const scrollUp = this.lastScrollTopPosition - window.scrollY > 0;

    // Calculate if the user is scrolling up and if the tab is visible
    const isInvisible =
      this.ui.prevPageBtn.getBoundingClientRect().top + this.ui.flightSearchTab.getBoundingClientRect().height <
      window.scrollY;

    if (scrollUp && isInvisible) {
      if (!hasClass(this.ui.flightSearch, STICKY)) addClass(this.ui.flightSearch, STICKY);

      // Dispatch a custom event to stop header overlap
      this.dispatchEvent(new CustomEvent(FRAFlights.AUTO_SCROLL, { bubbles: true }));
    } else {
      removeClass(this.ui.flightSearch, STICKY);
    }

    this.lastScrollTopPosition = window.scrollY;
  }

  get currentPage(): number {
    return this.state.currentPage;
  }

  get prevPage(): number {
    return this.state.prevPage;
  }

  /**
   * Returns provided api-url from custom element
   * @returns {string}
   */
  get apiUrl(): string {
    return this.getAttribute("data-api-url") || "";
  }

  /**
   * Returns provided api-url from custom element
   * @returns {string}
   */
  get flightDetailsUrl(): string {
    const targetDetailsDataUrl = `data-flight-${this.state.request.flighttype}-details-url`;
    return this.getAttribute(targetDetailsDataUrl) || "";
  }

  /**
   * Returns provided api-url from custom element
   * @returns {string}
   */
  get trainDetailsUrl(): string {
    const targetDetailsDataUrl = `data-train-${this.state.request.flighttype}-details-url`;
    return this.getAttribute(targetDetailsDataUrl) || "";
  }

  /**
   * Returns siteroot path
   * @returns {string}
   */
  get siteRootPath(): string {
    return this.getAttribute("data-flight-siteroot-path") || "";
  }

  /**
   * Returns state data applied as filter in request
   */
  get filterSettings(): RequestInfo | any {
    if (sessionStorage.getItem("restoreFlightOverview")) {
      const restoreObject = JSON.parse(sessionStorage.getItem("restoreFlightOverview")!);
      return restoreObject.formData;
    }
    return this.activeTab?.formData || this.ui.form.formData;
  }

  /**
   * Returns provided initial-count from custom element or the DEFAULT_INITIAL_COUNT
   * @returns {number}
   */
  get entriesPerPage(): number {
    const initialCount = parseInt(this.getAttribute("data-perpage") as string, 10);
    if (Number.isNaN(initialCount)) return FRAFlights.DEFAULT_ENTRIES_COUNT;
    return initialCount;
  }

  get language(): string {
    return FRAFlights.DEFAULT_INITIAL_LANG;
  }

  /**
   * Returns GET request string with config data and user input
   * for previous flights, perpage - fetch first 10 results
   * for next flights, perpage - get 10 flights per click
   * @returns {string}
   */
  requestData(): string {
    const filter = {
      perpage: this.entriesPerPage,
      lang: this.language,
      page: this.state.isPrevFlightReq ? this.prevPage : this.currentPage,
      ...this.filterSettings,
    };

    return new URLSearchParams(filter).toString();
  }

  /**
   * Generate request to fetch data from
   * @returns {string}
   */
  generateRequest(): string {
    return `${this.apiUrl}?${this.requestData()}`;
  }

  /**
   * Rendering Tablebody with lit
   */
  async renderTable(): Promise<void> {
    await I18nService.getInstance().waitForJson();
    const restoreObject = sessionStorage.getItem("restoreFlightOverview");
    const lastScroll = restoreObject ? JSON.parse(restoreObject).scrollPosition : window.scrollY;
    if (restoreObject) sessionStorage.removeItem("restoreFlightOverview");
    this.setFlightType(this.state.request.flighttype);
    const input = flightsTemplate(this.state.flights, this.flightDetailsUrl, this.trainDetailsUrl, this);
    render(input, this.ui.tableBody);
    // to avoid window jump to top of page, after table render
    if (this.currentPage > 1 || restoreObject) window.scrollTo(0, lastScroll);
  }

  setFlightType(type: string): void {
    this.ui.tableFlighttypeLabel.innerText =
      type === "arrivals"
        ? I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.flightlabel.arrivals")
        : I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.flightlabel.departures");

    if (type === "arrivals") addClass(this.ui.table, "fra-m-flights__td-departures");
  }

  /**
   * Fetch data from API and store response in state
   */
  async fetchFlights(): Promise<void> {
    const requestData = this.generateRequest();
    const firstRequest = this.state.previousRequest === "";
    // check if this request was fired already
    if (this.state.previousRequest === requestData) {
      if (sessionStorage.getItem("restoreFlightOverview")) sessionStorage.removeItem("restoreFlightOverview");
      return;
    }
    this.setState({ previousRequest: requestData });
    // fetch
    await fetchJSON<FlightsApiResponse>(requestData)
      .then(apiResponse => {
        let flights;
        if (this.state.isPrevFlightReq) {
          flights = apiResponse.data.concat(this.state.flights);
          this.disableEarlierButton(apiResponse.hasprev);
        } else if (this.currentPage > 1) {
          flights = this.state.flights.concat(apiResponse.data);
        } else if (apiResponse.data.length > 0) {
          flights = apiResponse.data;
        } else {
          this.renderNoResultsMessage();
        }
        if (apiResponse.data.length > 0) {
          // checks if error message is still visible
          this.hideMessage();
        }
        if (this.currentPage <= 1 && (!firstRequest || getCurrentMQ() === "MQ1")) this.scrollResultsIntoView();
        this.setState({ flights, isPrevFlightReq: false });
      })
      .catch(async () => {
        await I18nService.getInstance().waitForJson();
        const message = I18nService.getInstance().getKey("fraport.travel.components.content.flight.search.error.label");
        this.renderErrorMessage(message);
      });
  }

  scrollResultsIntoView() {
    const pageHeader = find(document as unknown as HTMLElement, ".fra-m-sticky-page-header-container");
    const pageHeaderHeight = pageHeader ? pageHeader.clientHeight : 0;
    window.scrollTo({ top: window.scrollY + this.getBoundingClientRect().top - pageHeaderHeight, behavior: "smooth" });
  }

  /**
   * render message if no results are available
   */
  async renderNoResultsMessage() {
    await I18nService.getInstance().waitForJson();
    const message = I18nService.getInstance().getKey("fraport.travel.components.content.flight.search.noResults.label");
    addClass(this, "fra-m-flights--hide-table");
    render(noResultsMessageTemplate(message), this.ui.flightSearchNoResultsMessage);
    addClass(this.ui.flightSearchNoResultsMessage, ACTIVE);
  }

  /**
   * render technical error message and hides buttons & table
   * @param {string} message
   */
  renderErrorMessage(message: string): void {
    addClass(this.ui.flightSearchErrorMessage, ACTIVE);
    addClass(this, "fra-m-flights--hide-table");
    addClass(this.ui.flightSearchErrorMessage, "fra-m-flights-search__error--escalated");
    render(errorMessageTemplate(message), this.ui.flightSearchErrorMessage);
  }

  /**
   * hides message if still visible
   */
  hideMessage(): void {
    if (hasClass(this, "fra-m-flights--hide-table")) {
      removeClass(this, "fra-m-flights--hide-table");

      if (this.ui.flightSearchErrorMessage) {
        removeClass(this.ui.flightSearchErrorMessage, ACTIVE);
        removeClass(this.ui.flightSearchErrorMessage, "fra-m-flights-search__error--escalated");
      }

      if (this.ui.flightSearchNoResultsMessage) {
        removeClass(this.ui.flightSearchNoResultsMessage, ACTIVE);
      }
    }
  }

  /**
   * Event listener for changes in search form
   * @param {CustomEvent} e
   */
  handleFormUpdate(e: CustomEvent): void {
    this.activeTab = e.detail.target;
    // filter have been set, reset page count to 1
    this.setState({ currentPage: 1, prevPage: 0 });
    removeClass(this.ui.prevPageBtn, DISABLED);
    // will trigger fetch
    this.setState({ request: this.activeTab.formData });
  }

  toggleActiveDialogEvent(e: CustomEvent) {
    const isTimePickerOpen = e.detail.active;
    if (isTimePickerOpen) {
      return addClass(this.ui.flightSearch, "fra-m-flights__time-dialog-active");
    }
    return removeClass(this.ui.flightSearch, "fra-m-flights__time-dialog-active");
  }

  /**
   * Click handler to load more entries
   * @param {MouseEvent | TouchEvent} e
   */
  handleLoadMore(e?: MouseEvent | TouchEvent): void {
    if (e) e.preventDefault();
    this.ui.loadMoreBtn.blur();

    const currentPage = this.currentPage + 1;
    // will trigger fetch
    this.setState({ currentPage });
  }

  /**
   * Click handler to load previous page
   * @param {MouseEvent | TouchEvent} e
   */
  handlePrevPage(e?: MouseEvent | TouchEvent): void {
    if (e) e.preventDefault();
    this.ui.prevPageBtn.blur();

    const prevPage = this.prevPage - 1;
    // set state and trigger fetch
    this.setState({ prevPage, isPrevFlightReq: true });
    this.fetchFlights();
  }

  handleFlightDetailClick(): void {
    const restoreObject = {
      scrollPosition: this.state.currentPage > 1 ? 0 : window.scrollY,
      formData: this.state.request,
    };
    sessionStorage.setItem("restoreFlightOverview", JSON.stringify(restoreObject));
  }

  /**
   * Function to check if the "Earlier" button should be disabled
   * @param {boolean} hasPrev
   * @returns {void}
   */
  private disableEarlierButton(hasPrev: boolean): void {
    if (!hasPrev) {
      addClass(this.ui.prevPageBtn, DISABLED);
    }
    return;
  }
}

customElements.define("fra-m-flights", FRAFlights);

export default FRAFlights;
